const { MinimogThemeStyles, MinimogThemeScripts } = window;

class QuickView {
  constructor() {
    this.modal = new MinimogTheme.Modal();
    this.isOpen = false;
    this.isLoading = false;

    // Event delegation for quick view button
    addEventDelegate({
      selector: ".m-product-quickview-button",
      handler: (e, target) => {
        e.preventDefault();
        if (this.isLoading) return;
        this.target = target;
        this.toggleLoading(true);
        const productHandle = target.dataset.productHandle;
        if (productHandle) this.loadProductQuickView(productHandle);
      },
    });

    // Vayve: quick view HTML is fetched ahead of the tap (hero swatches when the page is idle,
    // any quick view button on touch/hover) and kept as text, so opening is instant.
    this.htmlCache = new Map();
    const warm = (e) => {
      const btn = e.target && e.target.closest && e.target.closest(".m-product-quickview-button");
      if (btn && btn.dataset.productHandle) this.prefetch(btn.dataset.productHandle, "high");
    };
    ["touchstart", "pointerdown", "mouseover", "focusin"].forEach((type) =>
      document.addEventListener(type, warm, { passive: true, capture: true })
    );
    this.prefetchHero();

    // Subscribe to cart update events
    window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
      if (this.modal) this.modal.close();
    });
    window.MinimogEvents.subscribe('ON_MODAL_CLOSED', (modal) => {
      this.close();
    });
  }

  quickViewUrl(productHandle) {
    const url = new URL(`${window.MinimogSettings.base_url}products/${productHandle}`, window.location.href);
    url.searchParams.set("section_id", "product-quickview");
    return url.toString();
  }

  // Starts (once) the request for a product's quick view; resolves with its HTML text.
  // priority: "low" for background fetches, so they never compete with the page's own loading.
  fetchQuickView(productHandle, priority) {
    return fetch(this.quickViewUrl(productHandle), { ...getRequestDefaultConfigs(), priority }).then((res) => {
      if (!res.ok) throw new Error(`Failed to load quick view: ${res.status}`);
      return res.text();
    });
  }

  prefetch(productHandle, priority = "low") {
    let pending = this.htmlCache.get(productHandle);
    if (!pending) {
      pending = this.fetchQuickView(productHandle, priority);
      pending.catch(() => this.htmlCache.delete(productHandle));
      this.htmlCache.set(productHandle, pending);
      if (!this.assetsWarmed) {
        this.assetsWarmed = true;
        pending.then((text) => this.warmAssets(text)).catch(() => {});
      }
    }
    return pending;
  }

  // Downloads a stylesheet into the browser cache once; resolves when done (or failed).
  warmStyle(href) {
    this.styleCache = this.styleCache || new Map();
    let pending = this.styleCache.get(href);
    if (!pending) {
      pending = fetch(href, { credentials: "same-origin" }).then((res) => res.text()).catch(() => {});
      this.styleCache.set(href, pending);
    }
    return pending;
  }

  // Downloads the quick view's scripts and styles into the browser cache (not executed).
  warmAssets(text) {
    const found = new Set();
    text.replace(/(?:src|href)="([^"]+\/assets\/[^"]+\.(?:js|css)[^"]*)"/g, (m, href) => found.add(href.replace(/&amp;/g, "&")));
    found.forEach((href) => {
      if (/\.css(\?|$)/.test(href)) {
        this.warmStyle(new URL(href, window.location.href).href);
        return;
      }
      if (document.querySelector(`script[src="${href}"], link[href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = href;
      document.head.appendChild(link);
    });
  }

  // When the page is idle, fetch the quick views behind the hero's group swatches one by one
  // (the visible slide first). Skipped when the visitor asked to save data.
  prefetchHero() {
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ""))) return;
    const start = () => {
      const buttons = Array.from(document.querySelectorAll(".c-heroslider__root .m-product-quickview-button"));
      buttons.sort((a, b) => Number(!a.closest(".is-active")) - Number(!b.closest(".is-active")));
      const handles = [...new Set(buttons.map((b) => b.dataset.productHandle).filter(Boolean))].slice(0, 12);
      const next = () => {
        const handle = handles.shift();
        if (handle) this.prefetch(handle).catch(() => {}).finally(next);
      };
      next();
    };
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(start, { timeout: 3000 }) : setTimeout(start, 1500));
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });
  }

  // Load product quick view HTML
  loadProductQuickView(productHandle) {
    this.isLoading = true;
    // Use the background copy when it is (nearly) ready; otherwise also ask again at high
    // priority after 600 ms and take whichever arrives first.
    const pending = this.prefetch(productHandle, "high");
    let settled = false;
    pending.then(() => (settled = true), () => (settled = true));
    const backup = new Promise((resolve, reject) =>
      setTimeout(() => (settled ? pending : this.fetchQuickView(productHandle, "high")).then(resolve, reject), 600)
    );
    Promise.any([pending, backup])
      .then((text) => generateDomFromString(text))
      .then((html) => {
        this.modalContent = html.querySelector("#MainProduct-quick-view__content");
        const productInfo = html.querySelector("product-info");
        const colorScheme = productInfo.dataset.colorScheme;
        // Vayve: the "sheet" quick view style adds its own modal class and sizes itself in CSS.
        const extraClass = productInfo.dataset.cQvModal || "";
        // Opened again from inside an open quick view (e.g. a grouped product swatch):
        // swap the content in place instead of closing and reopening the modal.
        const swapInPlace = this.isOpen && this.modal.modal.isConnected && this.modal.modal.classList.contains("m-open-modal");
        if (swapInPlace) this.modal.removeChild();
        this.modal.appendChild(this.modalContent);
        if (extraClass) {
          this.modal.modalContent.style.removeProperty("width");
        } else {
          this.modal.setWidth("960px");
        }
        this.modal.modal.classList.toggle("c-qv-wrap", !!extraClass);

        const sectionWrapper = this.modal.modal.querySelector('#MainProduct-quick-view__content');

        sectionWrapper.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        // Vayve: open only once the quick view's stylesheets are downloaded (max 1.5 s), so a
        // first-time visitor never sees the content unstyled for a moment. The links inside the
        // modal only load once it is shown, so wait on our own download of the same files.
        const links = Array.from(sectionWrapper.querySelectorAll('link[rel="stylesheet"]'));
        const stylesReady = Promise.all(links.map((link) => this.warmStyle(link.href)));
        return Promise.race([stylesReady, new Promise((resolve) => setTimeout(resolve, 1500))]).then(() => {
          if (swapInPlace) {
            this.modal.modalContent.scrollTop = 0;
          } else {
            this.modal.open();
          }
          this.modal.setSizes(`m-gradient ${colorScheme}${extraClass ? ` ${extraClass}` : ""}`);

          this.toggleLoading(false);
          this.isOpen = true;
          this.isLoading = false; // Reset loading state
        });
      })
      .then(() => {
        document.dispatchEvent(
          new CustomEvent('tcustomizer-custom-show-quick-view', {
            detail: { productHandle: this.target.dataset.productHandle }
          })
        );

        document.dispatchEvent(
          new CustomEvent("quick-view:loaded", {
            detail: { productUrl: this.target.dataset.productHandle }
          })
        );
      })
      .catch((error) => {
        this.toggleLoading(false);
        this.isLoading = false;
        console.error("Error loading product quick view:", error);
      });
  }

  // Close the quick view modal
  close() {
    this.isOpen = false;
  }

  // Toggle loading spinner on the target element
  toggleLoading(isLoading) {
    if (isLoading) {
      this.target.classList.add("m-spinner-loading");
    } else {
      this.target.classList.remove("m-spinner-loading");
    }
  }
}

// Initialize the QuickView instance
MinimogTheme.ProductQuickView = new QuickView();
